import { useCallback, useEffect, useRef, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../integrations/firebase';
import { fetchCallStatus } from './callStatus';
import { DIAL_EVENT } from './dialViaPhone';

const m10 = (m) => String(m || '').replace(/\D/g, '').slice(-10);
// Calls dialled straight from a handset take no reservation, so nothing moves to
// tell us; a slow look-again while the page is visible catches them.
const POLL_MS = 90_000;

/**
 * Live "who called last / who is on them now" for one customer.
 *
 * Refreshes the moment a call starts or ends on this customer anywhere in the team:
 * every desk dial takes, and every logged outcome releases, tc/reservations/{mobile}
 * (staff-readable), so a listener on that one tiny node is the doorbell.
 * `status` stays null when the check cannot be read (no telecaller access, network).
 */
export function useCallStatus(mobile, { enabled = true } = {}) {
    const m = m10(mobile);
    const [status, setStatus] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const timer = useRef(null);
    const seq = useRef(0);

    const load = useCallback(async () => {
        if (m.length !== 10) return;
        const n = ++seq.current;
        const s = await fetchCallStatus(m, { timeoutMs: 6000 });
        if (n !== seq.current) return;                // a newer read answered first
        setStatus(s); setLoaded(true);
    }, [m]);

    useEffect(() => {
        setStatus(null); setLoaded(false);
        if (!enabled || m.length !== 10) return undefined;
        load();
        const soon = (ms = 1500) => { clearTimeout(timer.current); timer.current = setTimeout(load, ms); };
        let first = true;
        const unsub = onValue(ref(db, `tc/reservations/${m}`), () => {
            if (first) { first = false; return; }      // the initial value is what load() just read
            soon();
        }, () => { /* no access: the poll still runs */ });
        const poll = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
        // This agent's own dial: look again once it has had time to land.
        const onDial = (e) => {
            const d = e.detail || {};
            if (d.mobile === m && ['sent', 'refused', 'offline'].includes(d.phase)) soon(4000);
        };
        window.addEventListener(DIAL_EVENT, onDial);
        return () => { unsub(); clearInterval(poll); clearTimeout(timer.current); window.removeEventListener(DIAL_EVENT, onDial); };
    }, [m, enabled, load]);

    return { status, loaded, reload: load };
}

export default useCallStatus;
